import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import { DeleteTeam } from '../../apis/settings';
import { DeleteTeamGroup, GetSettingsTeamsPage } from '../../apis/teamGroups';
import { invalidateTeamGroups } from '@/hooks/useTeamGroups';
import type { SettingsTeamRow as CompanyTeam } from '../../apis/teamGroups';
import { Dropdown, Flex, message, Table, Row, Col, Typography } from 'antd';
import type { MenuProps } from 'antd';
import {
  CustomPagination,
  CommonDrawer,
  PageHeaderActions,
} from '../../components';
import { useToken, getPermissions } from '../../common/helper';
import { useSpin } from '../../components/SpinContext';
import TeamForm from './TeamForm';
import GroupForm, { type GroupData } from './GroupForm';
import AddButton from '@/components/AddButton';
import './SettingsTeams.css';
import { CustomDeleteModal } from '../Modals';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState, AppDispatch } from '../../redux-toolkit/store';
import { fetchTeamsDDL } from '../../redux-toolkit/slice/teamsSlice';
import theme from '@/assets/theme';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  DeleteIcon,
  PencilIcon,
} from '@/assets/icons/icons';
import { useTableScroll } from '../../hooks/useTableScroll';
import './SettingsTeams.css';

/** A group or team row; a group's teams sit in `children` (absent for teams). */
export type TeamTreeRow = CompanyTeam & {
  children?: TeamTreeRow[];
  /** Rendered under its group. */
  isMember?: boolean;
};

/**
 * Nests the API's flat rows: a team whose group is in the list goes under that
 * group's `children`; a team whose group isn't in the list stays top-level.
 */
export const buildTeamTree = (rows: CompanyTeam[]): TeamTreeRow[] => {
  const nodes: TeamTreeRow[] = rows.map((row) => ({ ...row }));
  const groupsById = new Map(
    nodes.filter((node) => node.is_group).map((node) => [node.id, node])
  );
  const roots: TeamTreeRow[] = [];
  nodes.forEach((node) => {
    const group =
      !node.is_group && node.group_id
        ? groupsById.get(node.group_id)
        : undefined;
    if (group) {
      node.isMember = true;
      // Nodes are copies, so pushing into their own children array is safe and
      // keeps the build linear for large groups.
      (group.children ??= []).push(node);
    } else {
      roots.push(node);
    }
  });
  return roots;
};

/** Ids of the rows that have team rows to expand. */
const getExpandableIds = (tree: TeamTreeRow[]): string[] =>
  tree.filter((row) => (row.children?.length ?? 0) > 0).map((row) => row.id);

const EllipsisText = ({ text }: { text: string }) => (
  <Typography.Text
    className='font-12 fw-600 c-pointer'
    ellipsis={{ tooltip: text }}
  >
    {text}
  </Typography.Text>
);

/**
 * SettingsTeams component - manages team list with CRUD operations
 */
const SettingsTeams = () => {
  const { token, removeToken } = useToken();
  const navigate = useNavigate();
  const { showSpin, hideSpin } = useSpin();
  const [teamsList, setTeamsList] = useState<CompanyTeam[]>([]);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [currentTeam, setCurrentTeam] = useState<CompanyTeam | null>(null);
  const [deleteModalVisible, setDeleteModalVisible] = useState<boolean>(false);
  const [selectedTeam, setSelectedTeam] = useState<CompanyTeam | null>(null);
  const [canManageGroups, setCanManageGroups] = useState<boolean>(false);
  // Open with no group to add one.
  const [groupDrawer, setGroupDrawer] = useState<{
    group?: GroupData;
  } | null>(null);
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const dispatch = useDispatch<AppDispatch>();
  const main_menu = useSelector((state: RootState) => state.menu.items);
  const TeamsPermissions = getPermissions(main_menu, 'Settings', 'Teams');
  const [searchInList, setSerachInList] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [totalCount, setTotalCount] = useState<number>(0);
  const tableScroll = useTableScroll();
  const [pageSize] = useState<number>(50);

  const unauthorize = useCallback(
    (error: { status?: number }) => {
      if (error?.status === 401) {
        removeToken();
        const _ = navigate('/login');
      }
    },
    [navigate, removeToken]
  );

  //Call to fetch Teams
  const fetchTeams = useCallback(
    (search = '', page = 1) => {
      if (!token) return;
      showSpin();
      const _ = GetSettingsTeamsPage(token, search, page)
        .then((res) => {
          if (
            res?.data?.data?.is_success &&
            Array.isArray(res.data.data.data)
          ) {
            const rows = res.data.data.data;
            setTeamsList(rows);
            setTotalPages(res?.data?.data?.page_count ?? 0);
            setTotalCount(res?.data?.data?.total_count ?? 0);
            setCanManageGroups(!!res.data.data.can_manage_groups);
            // A search expands every group so its matching teams are visible.
            if (search) setExpandedKeys(getExpandableIds(buildTeamTree(rows)));
          }
        })
        .catch((error) => {
          console.error('Error fetching teams:', error);
          unauthorize(error);
        })
        .finally(() => hideSpin());
    },
    [hideSpin, showSpin, token, unauthorize]
  );

  const getAllTeams = useCallback(() => {
    if (token) {
      const _ = dispatch(fetchTeamsDDL(token));
    }
    // Runs after every create/update/delete: groups in pickers may change.
    invalidateTeamGroups();
    fetchTeams(searchInList, page);
  }, [dispatch, fetchTeams, page, searchInList, token]);

  //fetch teams and update redux
  useEffect(() => {
    setTeamsList([]);
    getAllTeams();
  }, [token, page, searchInList, getAllTeams]);

  //Delete Action
  const handleDeleteClick = (team: CompanyTeam) => {
    setSelectedTeam(team);
    setDeleteModalVisible(true);
  };

  const handleConfirmDelete = () => {
    if (!selectedTeam || !token) return;
    showSpin();
    const remove = selectedTeam.is_group ? DeleteTeamGroup : DeleteTeam;
    remove(token, selectedTeam.id)
      .then((res) => {
        if (res?.data?.data?.is_success) {
          message.success(res.data.data.message ?? 'Team deleted');
          setTeamsList((prevList) =>
            prevList.filter((team) => team.id !== selectedTeam.id)
          );
          getAllTeams();
        } else {
          message.error(res?.data?.data?.message ?? 'Delete failed');
        }
      })
      .catch((error) => {
        unauthorize(error);
        message.error(
          selectedTeam.is_group
            ? 'Failed to delete group'
            : 'Failed to delete team'
        );
      })
      .finally(() => {
        hideSpin();
        setDeleteModalVisible(false);
      });
  };

  //Edit Action
  const handleEdit = (record: CompanyTeam) => {
    if (record.is_group) {
      setGroupDrawer({
        group: { id: record.id, name: record.name, color: record.color },
      });
      return;
    }
    setCurrentTeam(record);
    setDrawerOpen(true);
  };

  //Pagination function for updating page State
  const handlePageChange = (newPage: number) => {
    setPage(() => {
      const validPage = newPage > 0 ? newPage : 1;
      return validPage;
    });
  };

  //Add Teams
  const handleAddTeam = () => {
    setCurrentTeam(null);
    setDrawerOpen(true);
  };

  //search
  const handleSearch = (data: string) => {
    setSerachInList(data);
    setTeamsList([]);
    setExpandedKeys([]);
    setPage(1);
  };

  const toggleExpanded = (id: string) => {
    setExpandedKeys((prev) =>
      prev.includes(id) ? prev.filter((key) => key !== id) : [...prev, id]
    );
  };

  const teamTree = useMemo(() => buildTeamTree(teamsList), [teamsList]);

  const rangeStart = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = totalCount === 0 ? 0 : Math.min(page * pageSize, totalCount);
  const labeltext = theme.themeType === 2 ? 'client' : 'team';
  const getDeleteMessage = (team: CompanyTeam | null): string => {
    if (team?.is_group) {
      const count = team.teams_count ?? 0;
      const removed = 'This group will be removed.';
      if (count === 0) return removed;
      return `${removed} Its ${count} ${labeltext}${count === 1 ? '' : 's'} will be moved to the top level.`;
    }
    if (team?.is_connected_with_anything) {
      return `Unable to delete ${labeltext}. Please remove all associated ${theme.themeType === 2 ? 'carriers' : 'transport providers'}, products, sensors, locations, and users before trying again.`;
    }
    return `All data and users assigned to this ${labeltext} will be removed.`;
  };

  const deleteMessage = getDeleteMessage(selectedTeam);
  const isLargeScreen = window.innerWidth >= 1600;
  //columns for table
  const columns: ColumnsType<TeamTreeRow> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: isLargeScreen ? undefined : 200,
      render: (text, record) => {
        const hasMembers = (record.children?.length ?? 0) > 0;
        const isExpanded = expandedKeys.includes(record.id);
        return (
          <Flex
            align='center'
            gap='8px'
            className={
              record.isMember
                ? 'min-width-name settings-teams__name settings-teams__member'
                : 'min-width-name settings-teams__name'
            }
          >
            <span
              className='settings-teams__color-dot'
              // eslint-disable-next-line react/forbid-dom-props -- each team's own color, from data
              style={{ backgroundColor: record.color }}
            ></span>
            <EllipsisText text={String(text ?? '')} />
            {hasMembers && (
              // At the cell's right edge, so chevrons line up across rows.
              <button
                type='button'
                className='settings-teams__chevron'
                aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${String(text ?? '')}`}
                aria-expanded={isExpanded}
                onClick={() => toggleExpanded(record.id)}
              >
                {isExpanded ? (
                  <ChevronUpIcon size={16} color='var(--text-secondary)' />
                ) : (
                  <ChevronDownIcon size={16} color='var(--text-secondary)' />
                )}
              </button>
            )}
          </Flex>
        );
      },
    },
    {
      title: (
        <Typography.Text
          ellipsis={{
            tooltip: theme.themeType === 2 ? 'Carriers' : 'Transport providers',
          }}
          style={{ display: 'block', color: 'var(--text-secondary)' }}
        >
          {theme.themeType === 2 ? 'Carriers' : 'Transport providers'}
        </Typography.Text>
      ),
      dataIndex: 'transport_providers',
      key: 'transport_providers',
      render: (value) => value ?? 0,
    },
    {
      title: (
        <Typography.Text
          ellipsis={{ tooltip: true }}
          style={{ display: 'block', color: 'var(--text-secondary)' }}
        >
          Products
        </Typography.Text>
      ),
      dataIndex: 'products',
      key: 'products',
      render: (value) => value ?? 0,
    },
    {
      title: (
        <Typography.Text
          ellipsis={{ tooltip: true }}
          style={{ display: 'block', color: 'var(--text-secondary)' }}
        >
          Sensors
        </Typography.Text>
      ),
      dataIndex: 'sensors',
      key: 'sensors',
      render: (value) => value ?? 0,
    },
    {
      title: (
        <Typography.Text
          ellipsis={{ tooltip: true }}
          style={{ display: 'block', color: 'var(--text-secondary)' }}
        >
          Locations
        </Typography.Text>
      ),
      dataIndex: 'locations',
      key: 'locations',
      render: (value) => value ?? 0,
    },
    {
      title: 'Users',
      dataIndex: 'users',
      key: 'users',
      render: (value) => value ?? 0,
    },
    {
      title: (
        <Typography.Text
          ellipsis={{ tooltip: true }}
          style={{ display: 'block', color: 'var(--text-secondary)' }}
        >
          Shipments
        </Typography.Text>
      ),
      dataIndex: 'shipments',
      key: 'shipments',
      render: (value) => value ?? 0,
    },
    {
      title: (
        <Typography.Text
          ellipsis={{ tooltip: true }}
          style={{ display: 'block', color: 'var(--text-secondary)' }}
        >
          Notifications
        </Typography.Text>
      ),
      dataIndex: 'notifications',
      key: 'notifications',
      render: (value) => value ?? 0,
    },
    {
      title: '',
      key: 'action',
      render: (_, record) => (
        <Flex align='center' gap={6}>
          {record?.permissions?.is_update && (
            <Flex
              justify='center'
              align='center'
              className='action-icon-wrapper'
              role='button'
              aria-label='Edit'
              onClick={() => handleEdit(record)}
            >
              <PencilIcon color='var(--text-primary)' />
            </Flex>
          )}
          {record?.permissions?.is_delete && (
            <>
              <Flex
                justify='center'
                align='center'
                className='action-icon-wrapper'
                role='button'
                aria-label='Delete'
                onClick={() => handleDeleteClick(record)}
              >
                <DeleteIcon />
              </Flex>
            </>
          )}
        </Flex>
      ),
    },
  ];

  // Default admins get one Add button that asks what to add.
  const addMenuItems: MenuProps['items'] = [
    ...(TeamsPermissions?.is_create
      ? [
          {
            key: 'team',
            label: theme.themeType === 2 ? 'Client' : 'Team',
            onClick: handleAddTeam,
          },
        ]
      : []),
    { key: 'group', label: 'Group', onClick: () => setGroupDrawer({}) },
  ];

  return (
    <>
      <div className='settings-content-wrapper'>
        <PageHeaderActions
          title={`${theme.themeType === 2 ? 'Clients' : 'Teams'}`}
          showToggle={false}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          onSearchChange={handleSearch}
          onSearchClear={() => handleSearch('')}
          totalCount={totalCount}
          showCount={true}
          showAddButton={TeamsPermissions?.is_create || canManageGroups}
          onAddClick={handleAddTeam}
          showSearch={true}
          addButtonWrapper={
            canManageGroups ? (
              <Dropdown trigger={['click']} menu={{ items: addMenuItems }}>
                <div>
                  <AddButton fontSize='12px' fontWeight='600' />
                </div>
              </Dropdown>
            ) : undefined
          }
        ></PageHeaderActions>
        <Table<TeamTreeRow>
          columns={columns}
          dataSource={teamTree}
          rowKey='id'
          expandable={{
            expandedRowKeys: expandedKeys,
            onExpandedRowsChange: (keys) => setExpandedKeys(keys.map(String)),
            // The chevron beside the name toggles rows; no default icon or column.
            expandIcon: () => null,
            // Teams are indented inside the name cell (see settings-teams__member);
            // antd's indent span sits on its own line before the flex cell.
            indentSize: 0,
          }}
          pagination={false}
          className='settings-table settings-table-action-cols'
          rowHoverable={false}
          scroll={tableScroll}
          tableLayout='fixed'
        />

        <Row className='settings-teams-pagination-row'>
          <Col className='flex-to-block' xs={24}>
            {teamsList?.length > 0 && (
              <Flex className='pagination-alignment'>
                <CustomPagination
                  page={page}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </Flex>
            )}
          </Col>
        </Row>
      </div>

      {drawerOpen && (
        <CommonDrawer
          className='team-settings-drawer'
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
        >
          <TeamForm
            teamData={currentTeam ?? undefined}
            onClose={() => setDrawerOpen(false)}
            refreshTeams={getAllTeams}
            canManageGroups={canManageGroups}
          />
        </CommonDrawer>
      )}
      {groupDrawer && (
        <CommonDrawer open onClose={() => setGroupDrawer(null)}>
          <GroupForm
            groupData={groupDrawer.group}
            onClose={() => setGroupDrawer(null)}
            onSaved={getAllTeams}
          />
        </CommonDrawer>
      )}
      <CustomDeleteModal
        visible={deleteModalVisible}
        onClose={() => setDeleteModalVisible(false)}
        onConfirm={handleConfirmDelete}
        title={
          selectedTeam?.is_group
            ? 'Remove group'
            : selectedTeam?.is_connected_with_anything
              ? 'Action needed'
              : `Remove ${theme.themeType === 2 ? 'Client' : 'Team'}`
        }
        message={deleteMessage}
        cancelText='Cancel'
        confirmText={selectedTeam?.is_connected_with_anything ? '' : 'Remove'}
        confirmColor='var(--danger)'
      />
    </>
  );
};

export default SettingsTeams;
